import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { EventItem, Place } from '../types/shared/event';
import { HOTEL_ID } from './supabase/configClient';

interface OccurrenceRow {
  id: string;
  show_id: string;
  occurrence_type: string;
  occurrence_date: string;
  start_time: string | null;
  end_time: string | null;
  recurrence_end: string | null;
  recurrence_pattern: string | null;
  recurrence_days: string | null;
  price_info: string | null;
}

interface PlaceRow {
  id: string;
  name: string;
  description: string | null;
  fulladress: string | null;
  telephone: string | null;
  latitude: number | null;
  longitude: number | null;
  distance: number | null;
  timewalk: number | null;
  timedrive: number | null;
  timebike: number | null;
}

interface ShowRow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  type: string | null;
  duration: number | null;
  image_metadata: { url: string; size: number; type: string } | null;
  url: string | null;
  booking: string | null;
  offer: string | null;
  price_info: string | null;
  place_id: string;
  event_places: PlaceRow;
}

function expandOccurrences(
  occurrences: OccurrenceRow[],
  startFilter?: Date,
  endFilter?: Date
): Array<OccurrenceRow & { expanded_date: string }> {
  const results: Array<OccurrenceRow & { expanded_date: string }> = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  for (const occ of occurrences) {
    if (occ.occurrence_type === 'single') {
      results.push({ ...occ, expanded_date: occ.occurrence_date });
      continue;
    }

    if (occ.occurrence_type === 'recurring') {
      const occDate = new Date(occ.occurrence_date + 'T00:00:00');
      const recEnd = occ.recurrence_end
        ? new Date(occ.recurrence_end + 'T00:00:00')
        : maxDate;
      const pattern = occ.recurrence_pattern || 'daily';
      const recDays = occ.recurrence_days?.toLowerCase() || '';

      const current = new Date(occDate);

      while (current <= recEnd && current <= maxDate) {
        const dateStr = current.toISOString().split('T')[0];

        let dayMatch = true;
        if (pattern === 'weekly' && recDays) {
          const dayName = current.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase().trim();
          dayMatch = recDays.includes(dayName);
        }

        if (dayMatch) {
          results.push({ ...occ, expanded_date: dateStr });
        }

        if (pattern === 'daily') {
          current.setDate(current.getDate() + 1);
        } else if (pattern === 'weekly') {
          current.setDate(current.getDate() + 1);
        } else if (pattern === 'monthly') {
          current.setMonth(current.getMonth() + 1);
        } else {
          current.setDate(current.getDate() + 1);
        }
      }
    }
  }

  if (startFilter || endFilter) {
    return results.filter(r => {
      const startTime = r.start_time || '00:00:00';
      const endTime = r.end_time || '23:59:59';

      const eventStart = new Date(r.expanded_date + 'T' + startTime);
      const eventEnd = new Date(r.expanded_date + 'T' + endTime);

      if (startFilter && eventEnd < startFilter) return false;
      if (endFilter && eventStart > endFilter) return false;
      return true;
    });
  }

  return results;
}

class EventClient {
  private static instance: EventClient;

  private constructor() {}

  public static getInstance(): EventClient {
    if (!EventClient.instance) {
      EventClient.instance = new EventClient();
    }
    return EventClient.instance;
  }

  public async getEvents(sectionId: string, startDate?: string, endDate?: string): Promise<EventItem[]> {
    try {
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      const [showsResult, occurrencesResult, descriptionsResult, offersResult] = await Promise.all([
        supabaseClient
          .from('event_shows')
          .select('id, name, description, category, type, duration, image_metadata, url, booking, offer, price_info, place_id, event_places(id, name, description, fulladress, telephone, latitude, longitude, distance, timewalk, timedrive, timebike, google_map)')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),

        supabaseClient
          .from('event_occurrences')
          .select('id, show_id, occurrence_type, occurrence_date, start_time, end_time, recurrence_end, recurrence_pattern, recurrence_days, price_info')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),

        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'description'),

        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'offer')
      ]);

      if (showsResult.error) throw showsResult.error;
      if (occurrencesResult.error) throw occurrencesResult.error;

      const shows = showsResult.data as unknown as ShowRow[];
      const occurrences = occurrencesResult.data as OccurrenceRow[];

      const showsById = new Map<string, ShowRow>();
      for (const show of shows) {
        showsById.set(show.id, show);
      }

      const startFilter = startDate ? new Date(startDate) : undefined;
      const endFilter = endDate ? new Date(endDate) : undefined;
      const expandedOccurrences = expandOccurrences(occurrences, startFilter, endFilter);

      const descriptionsMap = new Map<string, Record<string, string>>();
      if (descriptionsResult.data) {
        for (const t of descriptionsResult.data) {
          const translations: Record<string, string> = {};
          for (const lang of ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru']) {
            if ((t as any)[lang]) translations[lang] = (t as any)[lang];
          }
          if ((t as any).item_id) descriptionsMap.set((t as any).item_id, translations);
          if ((t as any).idshow) descriptionsMap.set((t as any).idshow, translations);
        }
      }

      const offersMap = new Map<string, Record<string, string>>();
      if (offersResult.data) {
        for (const t of offersResult.data) {
          const translations: Record<string, string> = {};
          for (const lang of ['fr', 'en', 'es', 'it', 'pt', 'de', 'nl', 'ja', 'zh', 'ru']) {
            if ((t as any)[lang]) translations[lang] = (t as any)[lang];
          }
          if ((t as any).item_id) offersMap.set((t as any).item_id, translations);
          if ((t as any).idshow) offersMap.set((t as any).idshow, translations);
        }
      }

      const events: EventItem[] = [];

      for (const occ of expandedOccurrences) {
        const show = showsById.get(occ.show_id);
        if (!show || !show.event_places) continue;

        const place = show.event_places;
        const eventId = occ.occurrence_type === 'recurring'
          ? `${occ.id}-${occ.expanded_date.replace(/-/g, '')}`
          : occ.id;

        const descriptions = descriptionsMap.get(occ.id) || descriptionsMap.get(occ.show_id) || {};
        const offers = offersMap.get(occ.id) || offersMap.get(occ.show_id) || {};

        events.push({
          id: eventId,
          name: show.name,
          descriptions,
          description: show.description || '',
          offers,
          offer: show.offer || '',
          fulladress: place.fulladress || '',
          telephone: place.telephone || '',
          url: show.url || '',
          category: show.category || 'Autre',
          type: show.type || '',
          image: show.image_metadata?.url || '',
          latitude: place.latitude || 0,
          longitude: place.longitude || 0,
          distance: place.distance || 0,
          timewalk: place.timewalk?.toString() || '',
          timedrive: place.timedrive?.toString() || '',
          timebike: place.timebike?.toString() || '',
          booking: show.booking || '',
          price: occ.price_info || show.price_info || '',
          artiste: '',
          place: place.name,
          date: occ.expanded_date,
          hour: (occ.start_time || '00:00').substring(0, 5),
          idShow: occ.show_id,
          idshow: occ.show_id,
          show_id: occ.show_id,
          google_map: (place as any).google_map || '',
          source: 'event'
        } as EventItem);
      }

      events.sort((a, b) => {
        const todayStr = new Date().toISOString().split('T')[0];
        const aFuture = a.date >= todayStr;
        const bFuture = b.date >= todayStr;
        if (aFuture !== bFuture) return aFuture ? -1 : 1;
        const dateCompare = a.date.localeCompare(b.date);
        return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
      });

      return events;

    } catch (error) {
      logger.error('Error fetching events', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  public getPlaces(events: EventItem[]): Place[] {
    try {
      const placeMap = new Map<string, Place>();

      events.forEach(event => {
        if (!placeMap.has(event.place)) {
          placeMap.set(event.place, {
            name: event.place,
            latitude: event.latitude,
            longitude: event.longitude,
            distance: event.distance,
            fulladress: event.fulladress,
            itinerary: event.url,
            shows: []
          });
        }

        const place = placeMap.get(event.place)!;
        const showId = (event as any).idShow || (event as any).show_id || '';
        let show = place.shows.find(s => (s as any).idShow === showId);

        if (!show) {
          show = {
            id: event.id,
            idShow: showId,
            name: event.name,
            image: event.image,
            type: event.type,
            source: 'event',
            events: []
          } as any;
          place.shows.push(show);
        }

        (show as any).events.push(event);
      });

      const places = Array.from(placeMap.values()).map(place => ({
        ...place,
        shows: place.shows.sort((a, b) => {
          const aEvent = (a as any).events[0];
          const bEvent = (b as any).events[0];
          const dateCompare = aEvent.date.localeCompare(bEvent.date);
          return dateCompare !== 0 ? dateCompare : aEvent.hour.localeCompare(bEvent.hour);
        })
      }));

      return places;

    } catch (error) {
      logger.error('Error creating places', {
        category: 'API',
        data: { error }
      });
      return [];
    }
  }
}

const eventClient = EventClient.getInstance();

export { eventClient };
